package navigation.home.organization.organizationdetails

import addedittravelagent.AddEditTravelAgentEvent
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.ScreenKey
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.address.ui.AddressEvent
import com.essntl.features.contact.ui.ContactEvent
import com.essntl.features.contact.ui.ContactNavigation
import com.essntl.features.contact.ui.contactdetails.ContactDetailsScreen
import com.essntl.features.customfields.ui.CustomFieldsEvent
import com.essntl.features.organization.ui.OrganizationEvent
import com.essntl.features.organization.ui.OrganizationScreen
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsEvent
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsNavigation
import com.essntl.features.organization.ui.organizationdetails.OrganizationDetailsScreen
import com.essntl.features.service.ui.allservice.AllServiceEvent
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import navigation.home.address.AddressScreenModel
import navigation.home.contact.ContactScreenModel
import navigation.home.customfields.CustomFieldsScreenModel
import navigation.home.edititinerary.EditItineraryScreenNav
import navigation.home.organization.OrganizationScreenModel
import navigation.home.proposal.addeditproposal.AddEditProposalScreenNav
import navigation.home.service.allservice.AllServiceScreenModel
import navigation.home.travelagents.addedittravelagent.AddEditTravelAgentScreenModel
import navigation.utils.NavLinkScreen
import org.koin.core.qualifier.named

class OrganizationDetailsScreenNav(
    val organizationId: String,
) : NavLinkScreen {

    override val defaultTitle: String = "Organization Details"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Person,
        ),
    )

    override val key: ScreenKey = "OrganizationDetailsScreenNav/$organizationId"

    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val organizationDetailsScreenModel = koinScreenModel<OrganizationDetailsScreenModel>()
        val organizationDetailsState by organizationDetailsScreenModel.state.collectAsState()

        val organizationScreenModel = koinScreenModel<OrganizationScreenModel>()
        val organizationState by organizationScreenModel.state.collectAsState()

        val organizationAddressScreenModel = koinScreenModel<AddressScreenModel>(named("organization"))
        val organizationAddressState by organizationAddressScreenModel.state.collectAsState()

        val contactScreenModel = koinScreenModel<ContactScreenModel>()
        val contactState by contactScreenModel.state.collectAsState()

        val addressScreenModel = koinScreenModel<AddressScreenModel>(named("contact"))
        val addressState by addressScreenModel.state.collectAsState()

        val customFieldsScreenModel = koinScreenModel<CustomFieldsScreenModel>()
        val customFieldsState by customFieldsScreenModel.state.collectAsState()

        val travelAgentScreenModel = koinScreenModel<AddEditTravelAgentScreenModel>()
        val travelAgentState by travelAgentScreenModel.state.collectAsState()

        val allServiceScreenModel = koinScreenModel<AllServiceScreenModel>()
        val allServiceState by allServiceScreenModel.state.collectAsState()

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(Unit) {
            organizationDetailsScreenModel.onEvent(
                OrganizationDetailsEvent.GetOrganization(
                    id = organizationId,
                ),
            )

            allServiceScreenModel.onEvent(
                AllServiceEvent.GetAllService(
                    organizationId = organizationId,
                ),
            )
        }

        LaunchedEffect(organizationDetailsState.getOrganization) {
            val title = organizationDetailsState.getOrganization.data?.name

            navLinkState = if (title != null)
                navLinkState.copy(
                    title = title,
                    isLoading = false,
                )
            else
                navLinkState.copy(
                    isLoading = organizationDetailsState.getOrganization.isLoading,
                )
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                addressScreenModel.onEvent(AddressEvent.Clear)
                contactScreenModel.onEvent(ContactEvent.ClearContactState)
                customFieldsScreenModel.onEvent(CustomFieldsEvent.ClearCustomFieldsState)
                organizationDetailsScreenModel.onEvent(OrganizationDetailsEvent.SelectContact(""))
            }
        }

        LaunchedEffect(contactState.getContact.isSuccess) {
            if (contactState.getContact.isSuccess) {
                contactState.getContact.data?.organization?.let { o ->
                    organizationScreenModel.onEvent(
                        OrganizationEvent.UpdateOrganizationFields(
                            organization = o,
                        ),
                    )
                }

                val id = contactState.contact.value.address?.id
                if (id.isNullOrEmpty()) {
                    contactScreenModel.onEvent(ContactEvent.AddRemoveAddress(false))
                    addressScreenModel.onEvent(AddressEvent.Clear)
                } else {
                    contactScreenModel.onEvent(ContactEvent.AddRemoveAddress(true))
                    addressScreenModel.onEvent(
                        AddressEvent.GetAddress(
                            id = id,
                        ),
                    )
                }
            }
        }

        LaunchedEffect(organizationDetailsState.getOrganization.isSuccess) {
            if (organizationDetailsState.getOrganization.isSuccess) {
                val id = organizationDetailsState.organization.value.address?.id
                if (id.isNullOrEmpty()) {
                    organizationDetailsScreenModel.onEvent(OrganizationDetailsEvent.AddRemoveAddress(false))
                    organizationAddressScreenModel.onEvent(AddressEvent.Clear)
                } else {
                    organizationDetailsScreenModel.onEvent(OrganizationDetailsEvent.AddRemoveAddress(true))
                    organizationAddressScreenModel.onEvent(
                        AddressEvent.GetAddress(
                            id = id,
                        ),
                    )
                }
            }
        }

        LaunchedEffect(contactState.editContact.isSuccess) {
            if (contactState.editContact.isSuccess) {
                contactState.editContact.data?.let { c ->
                    if (c.organization?.id == organizationId)
                        organizationDetailsScreenModel.onEvent(
                            OrganizationDetailsEvent.UpdateContact(c),
                        )
                    else {
                        organizationDetailsScreenModel.onEvent(
                            OrganizationDetailsEvent.DeleteContact(c),
                        )

                        sidePanelState.sidePanelOpened = false
                    }
                }
            }
        }

        LaunchedEffect(customFieldsState.getTravelAgent.isSuccess) {
            if (customFieldsState.getTravelAgent.isSuccess) {
                val travelAgentId = customFieldsState.getTravelAgent.data?.id
                if (!travelAgentId.isNullOrEmpty()) {
                    travelAgentScreenModel.onEvent(
                        AddEditTravelAgentEvent.GetTravelAgent(
                            travelAgentId = travelAgentId,
                        ),
                    )
                }
            }
        }

        LaunchedEffect(organizationState.organization.value.id) {
            contactScreenModel.onEvent(
                ContactEvent.UpdateContactFields(
                    contactState.contact.value.copy(
                        organization = organizationState.organization.value,
                    ),
                ),
            )
        }

        fun handleNavigationEvent(navigationEvent: OrganizationDetailsNavigation) {
            when (navigationEvent) {
                is OrganizationDetailsNavigation.EditContact -> {
                    if (organizationDetailsState.selectedContactId == navigationEvent.id)
                        return

                    organizationDetailsScreenModel.onEvent(
                        OrganizationDetailsEvent.SelectContact(
                            navigationEvent.id,
                        ),
                    )

                    contactScreenModel.onEvent(
                        ContactEvent.GetContact(
                            navigationEvent.id,
                        ),
                    )

                    val organization = organizationDetailsState.organization.value
                    organization.type?.let { type ->
                        customFieldsScreenModel.onEvent(
                            CustomFieldsEvent.SetType(
                                type = type,
                                contactId = navigationEvent.id,
                            ),
                        )

                        customFieldsScreenModel.onEvent(
                            CustomFieldsEvent.GetTravelAgent(
                                contactId = navigationEvent.id,
                            ),
                        )
                    }

                    customFieldsState.getTravelAgent.data?.id?.let { travelAgentId ->
                        travelAgentScreenModel.onEvent(
                            AddEditTravelAgentEvent.GetTravelAgent(
                                travelAgentId = travelAgentId,
                            ),
                        )
                    }

                    sidePanelState.sidePanelOpened = true
                }

                is OrganizationDetailsNavigation.Toggle -> {
                    sidePanelState.sidePanelOpened = false
                }

                is OrganizationDetailsNavigation.EditProposal -> {
                    navigator.push(
                        AddEditProposalScreenNav(
                            proposalId = navigationEvent.proposalId,
                        ),
                    )
                }

                is OrganizationDetailsNavigation.EditItinerary -> {
                    navigator.push(
                        EditItineraryScreenNav(
                            itineraryId = navigationEvent.itineraryId,
                        ),
                    )
                }
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius)),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(
                                    color = MaterialTheme.esColorScheme.bgSecondary,
                                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                                )
                                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                                .padding(2.5f.spacing)
                                .fillMaxWidth(),
                    ) {
                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )

                        Text(
                            text = contactState.contact.value.prettyName,
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.h3Bold24,
                        )
                    }

                    Spacer(modifier = Modifier.height(1f.spacing))

                    Column(
                        verticalArrangement = Arrangement.spacedBy(1.spacing),
                    ) {
                        OrganizationScreen(
                            state = organizationState,
                            onEvent = organizationScreenModel::onEvent,
                            validation = organizationScreenModel.validation,
                            addressState = organizationAddressState,
                            onAddressEvent = organizationAddressScreenModel::onEvent,
                            addressValidation = organizationAddressScreenModel.validation,
                            creation = false,
                        )

                        ContactDetailsScreen(
                            state = contactState,
                            onEvent = contactScreenModel::onEvent,
                            onNavigation = {
                                when (it) {
                                    is ContactNavigation.EditProposal -> {
                                        handleNavigationEvent(OrganizationDetailsNavigation.EditProposal(it.proposalId))
                                    }

                                    is ContactNavigation.EditItinerary -> {
                                        handleNavigationEvent(OrganizationDetailsNavigation.EditItinerary(it.itineraryId))
                                    }
                                }
                            },
                            validation = contactScreenModel.validation,
                            addressState = addressState,
                            onAddressEvent = addressScreenModel::onEvent,
                            addressValidation = addressScreenModel.validation,
                            customFieldsState = customFieldsState,
                            travelAgentState = travelAgentState,
                            onCustomFieldsEvent = customFieldsScreenModel::onEvent,
                            isSaveEnabled = contactScreenModel.vm.isContactChanged() ||
                                addressScreenModel.vm.isAddressChanged() ||
                                customFieldsScreenModel.vm.isCustomFieldsChanged(),
                        )
                    }
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier = Modifier.width(1.spacing).fillMaxHeight(),
                        )
                    },
                ),
            modifier = Modifier.fillMaxSize(),
        ) {
            OrganizationDetailsScreen(
                state = organizationDetailsState,
                onEvent = organizationDetailsScreenModel::onEvent,
                addressState = organizationAddressState,
                onAddressEvent = organizationAddressScreenModel::onEvent,
                addressValidation = organizationAddressScreenModel.validation,
                allServiceState = allServiceState,
                onEventAllService = allServiceScreenModel::onEvent,
                onNavigation = { handleNavigationEvent(it) },
            )
        }
    }
}